import { useState, useEffect } from 'react';
import { getValueFromLs, setKeyValuePairToLS } from '@/shared/services/localstorage';
import { EDITOR_LS_CONSTANTS } from '@/editor/shared/constants/editor-LS.constants';
import { AvailablePlatformsEnum } from '@/editor/shared/constants/available-platforms';
import type { CreativeType } from '@/editor/shared/constants/creatives-types.enum';
import { useAppDispatch, useAppSelector } from '@/store/hooks/redux';
import type { IMediaLibraryVideoModel } from '@/shared/models/media-library/media-library-video.model';
import { useGetRetailPropertyDataQuery } from '@/editor/services/http/retail-data-provider.api';
import { useGetMediaLibraryImagesQuery } from '@/editor/services/http/media-library.api';
import { setMediaOnVideo } from '@/editor/store/reducers/creative-step.slice';
import debounce from 'lodash/debounce';

export const useMediaLibraryVideos = (videos?: IMediaLibraryVideoModel[], data?: IMediaLibraryVideoModel[]) => {
  const dispatch = useAppDispatch();

  const [search, setSearch] = useState<string>('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [videoData, setVideoData] = useState<IMediaLibraryVideoModel[]>(videos ?? []);

  const propertyExtId = useAppSelector((state) => state.propertyInfoSliceReducer.propertyExtId);
  const drawerState = useAppSelector((state) => state.editorModalsDrawersReducer.mediaLibraryDrawerState);
  const storedCompanyId = useAppSelector((state) => state.propertyInfoSliceReducer.companyId);
  const creativeFormsState = useAppSelector((state) => state.creativeStepSliceReducer.creativeFormsState);

  const propertyFromLS = getValueFromLs(EDITOR_LS_CONSTANTS.PROPERTY_INTERNAL_ID);

  const { data: imageData, isLoading: imageDataLoading } = useGetMediaLibraryImagesQuery(
    { propertyId: propertyFromLS! },
    { skip: !propertyFromLS }
  );

  const { data: propertyData } = useGetRetailPropertyDataQuery({
    propertyId: propertyExtId!,
    companyId: storedCompanyId,
  });

  const formDataValue = creativeFormsState[drawerState.platform!][drawerState.creativeType as CreativeType];

  useEffect(() => {
    const vidData: IMediaLibraryVideoModel[] | undefined = search
      ? data?.filter((el) => el.fileName.toLowerCase().includes(search.trim().toLowerCase()))
      : data;
    setVideoData(vidData || []);
  }, [data, search]);

  const handleClickListItem = (event: React.MouseEvent<HTMLElement>, item: IMediaLibraryVideoModel) => {
    if (drawerState.platform === AvailablePlatformsEnum.BIDTHEATRE) {
      setKeyValuePairToLS(EDITOR_LS_CONSTANTS.IMAGE_FOR_VIDEO, 'video');
    }
    dispatch(setMediaOnVideo({ platform: drawerState.platform!, media: item }));
  };

  const handleSearchChange = (value: string) => {
    debounce(setSearch)(value ?? '');
  };

  const handleTemplateSelection = (templateId: string) => {
    setSelectedTemplateId(templateId);
  };

  return {
    selectedTemplateId,
    videoData,
    imageData,
    propertyData,
    formDataValue,
    drawerState,
    imageDataLoading,
    handleClickListItem,
    handleSearchChange,
    handleTemplateSelection,
  };
};
